import { useEffect, useState } from 'react';
import { ClipboardSignature, Fingerprint } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { ManagementReviewInput } from '../../types/validation.types';

interface ManagementReviewDialogProps {
  open: boolean;
  defaultUsername: string;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (input: ManagementReviewInput) => Promise<void>;
}

export function ManagementReviewDialog({
  open,
  defaultUsername,
  isPending,
  onOpenChange,
  onConfirm,
}: ManagementReviewDialogProps) {
  const [title, setTitle] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [observations, setObservations] = useState('');
  const [decisions, setDecisions] = useState('');
  const [actionItems, setActionItems] = useState('');
  const [reason, setReason] = useState('');
  const [username, setUsername] = useState(defaultUsername);
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (!open) return;
    const today = new Date().toISOString().slice(0, 10);
    const quarterStart = new Date();
    quarterStart.setUTCMonth(quarterStart.getUTCMonth() - 3);
    setTitle(`eQMS management review · ${today}`);
    setPeriodStart(quarterStart.toISOString().slice(0, 10));
    setPeriodEnd(today);
    setObservations('');
    setDecisions('');
    setActionItems('');
    setReason('');
    setUsername(defaultUsername);
    setPassword('');
  }, [defaultUsername, open]);

  const canSubmit = title.trim().length >= 3
    && Boolean(periodStart)
    && Boolean(periodEnd)
    && periodStart <= periodEnd
    && observations.trim().length >= 3
    && decisions.trim().length >= 3
    && reason.trim().length >= 3
    && Boolean(username.trim())
    && Boolean(password);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-800">
            <ClipboardSignature className="h-5 w-5" />
          </div>
          <DialogTitle>Capture management review</DialogTitle>
          <DialogDescription>
            The server freezes current document, quality-event, training, and validation metrics
            at approval time. The snapshot cannot be rewritten later.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="review-title">Review title</Label>
            <Input
              id="review-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="review-period-start">Period start</Label>
              <Input
                id="review-period-start"
                type="date"
                value={periodStart}
                onChange={(event) => setPeriodStart(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="review-period-end">Period end</Label>
              <Input
                id="review-period-end"
                type="date"
                value={periodEnd}
                onChange={(event) => setPeriodEnd(event.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-observations">Observations</Label>
            <Textarea
              id="review-observations"
              value={observations}
              onChange={(event) => setObservations(event.target.value)}
              placeholder="Summarize trends, risks, overdue evidence, and material exceptions."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-decisions">Decisions</Label>
            <Textarea
              id="review-decisions"
              value={decisions}
              onChange={(event) => setDecisions(event.target.value)}
              placeholder="Record decisions, accepted risk, and escalation."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-actions">Action items</Label>
            <Textarea
              id="review-actions"
              value={actionItems}
              onChange={(event) => setActionItems(event.target.value)}
              placeholder="One action per line"
              rows={3}
            />
          </div>

          <div className="rounded-lg border border-indigo-100 bg-indigo-50/70 px-3 py-2 text-sm text-indigo-950">
            <div className="flex items-center gap-2 font-medium">
              <Fingerprint className="h-4 w-4" />
              Signature meaning: approval
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="review-signature-reason">Signature reason</Label>
            <Textarea
              id="review-signature-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="I approve this management review and its frozen source metrics."
              rows={2}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="review-signature-username">Username</Label>
              <Input
                id="review-signature-username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="review-signature-password">Password</Label>
              <Input
                id="review-signature-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="bg-indigo-800 text-white hover:bg-indigo-900"
            disabled={!canSubmit || isPending}
            onClick={() => onConfirm({
              title: title.trim(),
              periodStart,
              periodEnd,
              observations: observations.trim(),
              decisions: decisions.trim(),
              actionItems: actionItems
                .split('\n')
                .map((item) => item.trim())
                .filter(Boolean),
              meaning: 'approval',
              reason: reason.trim(),
              username: username.trim(),
              password,
            })}
          >
            {isPending ? 'Approving…' : 'Approve snapshot'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
